import { IdentityService, SessionService } from 'account-hybrid/core/authentication';
import { SpacesService } from 'account-hybrid/common/components/spaces';
import { DataGranularity } from 'account-hybrid/features/account-settings/enums/data-granularity.enum';
import {
    SpacetimeCatalog,
    SpacetimeInsightStatus,
} from 'account-hybrid/features/spacetime/models/spacetime-catalog.model';
import { SPACETIME_INSIGHTS, SpacetimeInsightKey } from 'account-hybrid/features/spacetime/models/spacetime-insight-definitions';

/**
 * Centralised access checks for Spacetime sub-features. Used by both the tabs nav (to hide tabs)
 * and UIRouter route guards (to block direct URL access). Keeping the rules in one place makes it
 * impossible for the menu and the router to drift apart.
 */

/**
 * Portfolio tab compares brands/properties across an account, so it only makes sense for users who
 * can see more than one property and whose company reports per-property (not at aggregate level).
 * Hides for: single-property accounts, property-only contexts, aggregate-level data granularity.
 */
export function canAccessPortfolio(
    spacesService: SpacesService,
    sessionService: SessionService
): boolean {
    const companyUuid = spacesService.getCompanyUuid();
    if (!companyUuid) return false;
    const company = sessionService.getCompany(companyUuid);
    if (!company || company.DataGranularity === DataGranularity.AggregateLevel) return false;
    const properties = sessionService.getCompanyProperties(companyUuid);
    return (properties?.length ?? 0) > 1;
}

/** Insights with a Spacetime view, in catalog order. The key is both the catalog key and the state suffix. */
export const SPACETIME_INSIGHT_TABS: readonly SpacetimeInsightKey[] = SPACETIME_INSIGHTS.filter(insight => insight.state).map(insight => insight.key);
export type SpacetimeInsightTab = SpacetimeInsightKey;

/** The insight a state belongs to (`spacetime.sfmc`, `spacetime.sfmc.detail`), or null when it is none. */
export function insightForState(stateName: string): SpacetimeInsightTab | null {
    const tab = /^spacetime\.([a-z-]+)(?:\.|$)/.exec(stateName)?.[1];
    return tab && (SPACETIME_INSIGHT_TABS as readonly string[]).includes(tab) ? tab as SpacetimeInsightTab : null;
}

/** Hoteliers open Unlocked insights, staff also Flip.to-only ones. Shared by the tab bar and the route hook. */
export function canOpenInsight(catalog: SpacetimeCatalog, insight: string, isFliptoStaff: boolean): boolean {
    switch (catalog.Insights.find(i => i.Key === insight)?.Status) {
        case SpacetimeInsightStatus.Unlocked:
            return true;
        case SpacetimeInsightStatus.FliptoOnly:
            return isFliptoStaff;
        default:
            return false;
    }
}

/**
 * Story playground is a Flip.to staff-only dev tool for iterating on AI-summary story rendering
 * from recorded responses — reachable by URL only, never in the tab bar.
 *
 * Has its own rule rather than borrowing another feature's: it previously rode the Audiences staff
 * gate, so opening that tab up to hoteliers would have handed them this tool too.
 */
export function canAccessStoryPlayground(identityService: IdentityService): boolean {
    return identityService.isFliptoStaff();
}
